import type { ReactNode } from 'react';
import './profileLayout.css';

/**
 * The three-column profile frame, for a lead, a deal or a company.
 *
 * ── The contract Dev 3 needs, stated once ──────────────────────────────────
 *
 * This component takes four slots and decides NOTHING about what goes in them.
 * It can therefore be mounted in a full page or inside a drawer without either
 * caller knowing which the other is doing, which is the whole point: Dev 3 is
 * building a Sales Tasks drawer that mounts a profile inside it, and if the
 * profile were hard-wired to a page grid one of us would redo the work.
 *
 * The column count comes from the SHELL'S OWN WIDTH, not the window's. See
 * profileLayout.css for why, and for where 760 and 1060 were measured.
 *
 * ── What a caller has to do ────────────────────────────────────────────────
 *
 * Render it, and give the box it sits in a definite height.
 *
 * The height is new, and it is a correction: the first version of this comment
 * said a caller had to do "nothing but render it". That was true when the
 * shell had no scroll of its own. It now scrolls each column independently at
 * three columns, which needs a height to divide up. A drawer body already has
 * one. A page route has to supply one. Where the height is missing the shell
 * degrades to a scrolling page rather than breaking, so this is a quality
 * requirement and not a crash, which is exactly why it is worth writing down.
 *
 * Still no layout prop, no width prop, no "isDrawer". If a caller ever needs
 * to force a column count, that is a signal the container is lying about its
 * width, and the honest fix is the container.
 *
 * ── Why identity and properties share one slot boundary ────────────────────
 *
 * They are rendered into a single column element, not two. HubSpot's left
 * column is one scroll region holding the name, the links, the icon actions
 * and every property card together, so scrolling the properties scrolls the
 * name away with them. Two scrollers that merely sit next to each other look
 * identical until the content is long, which is when it matters.
 */
export function ProfileShell({
  identity,
  properties,
  timeline,
  associations,
}: {
  /** Name, logo, the links, and the row of icon actions. Always at the top. */
  identity: ReactNode;
  /** The grouped, collapsible property cards. */
  properties: ReactNode;
  /** The activity feed. The only slot with unbounded height. */
  timeline: ReactNode;
  /** Associated records, as counted cards. */
  associations: ReactNode;
}) {
  return (
    // Two elements, and the nesting is load-bearing.
    //
    // .profile-shell DECLARES the container. .profile-shell-grid IS the grid
    // the @container rules size. They cannot be the same element: a container
    // query matches DESCENDANTS of its container and never the container
    // itself, so a rule targeting .profile-shell inside
    // `@container profile (...)` is silently dead.
    //
    // This shipped wrong in drop 146 and rendered one column at 984px of
    // available width. Nothing caught it: the stylesheet said exactly what it
    // was supposed to say, and jsdom has no layout engine to disagree. It was
    // found by measuring the deployed page. See profileLayout.test.ts, which
    // now fails if any @container rule names the container element.
    <div className="profile-shell">
      <div className="profile-shell-grid">
        <div className="profile-aside">
          <div className="profile-identity">{identity}</div>
          <div className="profile-properties">{properties}</div>
        </div>
        <div className="profile-timeline">{timeline}</div>
        <div className="profile-associations">{associations}</div>
      </div>
    </div>
  );
}
